import { Observable } from 'rxjs';
import {
  CommunicationLibraryEntrySummary,
  CommunicationMergeTag,
  CommunicationTemplate,
  CommunicationTemplatePreviewOptions,
  CommunicationTemplatePreviewResult,
  CommunicationTemplateSummary,
  CommunicationTemplateType,
  CommunicationTemplateVersion,
  CommunicationTemplateVersionSummary,
  CommunicationUserTemplateSummary,
  CreateUserTemplateFromLibraryRequest,
  DuplicateTemplateRequest,
  EmailFooterCompliance,
  OrganisationEmailFooter,
  OrganisationEmailReplyToSettings,
  SaveCommunicationTemplateRequest,
  SaveCommunicationTemplateVersionRequest,
  SaveUserTemplateRequest,
  TestSendRequest
} from './communication-template.models';

/**
 * The data-access contract the shared {@link CommunicationTemplateManagerComponent} depends on, so it
 * can serve both the SysAdmin route (any organisation) and the org-level Admin route (own organisation)
 * without knowing which. Every call is explicitly scoped by `organisationId`. Each host provides the
 * concrete implementation for this token (SysAdmin service vs Admin service).
 */
export abstract class CommunicationTemplateApi {
  abstract getTemplates(organisationId: number): Observable<CommunicationTemplateSummary[]>;
  abstract getTemplate(organisationId: number, templateItemId: number): Observable<CommunicationTemplate>;
  /** The platform Default (system-org) template content, used to seed an uncustomised type (Q4). */
  abstract getDefaultTemplate(organisationId: number, templateItemId: number): Observable<CommunicationTemplate>;
  abstract getMergeTags(organisationId: number): Observable<CommunicationMergeTag[]>;
  abstract getTypes(organisationId: number): Observable<CommunicationTemplateType[]>;
  abstract save(organisationId: number, request: SaveCommunicationTemplateRequest): Observable<CommunicationTemplate>;
  abstract getVersions(organisationId: number, templateItemId: number): Observable<CommunicationTemplateVersionSummary[]>;
  abstract getVersion(organisationId: number, versionId: number): Observable<CommunicationTemplateVersion>;
  abstract createVersion(
    organisationId: number,
    templateItemId: number,
    request: SaveCommunicationTemplateVersionRequest,
    activate: boolean
  ): Observable<CommunicationTemplateVersion>;
  abstract updateVersion(
    organisationId: number,
    versionId: number,
    request: SaveCommunicationTemplateVersionRequest
  ): Observable<CommunicationTemplateVersion>;
  abstract activateVersion(organisationId: number, templateItemId: number, versionId: number): Observable<unknown>;
  /** Names a version, or clears its name with null; nothing else about the version changes (SB-17138). */
  abstract renameVersion(
    organisationId: number,
    versionId: number,
    name: string | null
  ): Observable<CommunicationTemplateVersionSummary>;
  abstract deleteVersion(organisationId: number, versionId: number): Observable<unknown>;
  /**
   * Renders a preview of the email.
   *
   * `includeOrganisationFooter` shows the bottom of the email as a recipient would receive it — the
   * organisation's footer, then the mandatory compliance block (SB-14896). Pass the previewed
   * template's own setting: previewing the body alone is what let the footer/compliance ordering bug
   * survive until someone sent a real email.
   *
   * Every template is previewed as its test email shows it: the school's real data and the caller as the sender,
   * with sample recipient and event values marked "(sample)" (SB-17220). `options.isUserTemplate` shows a custom
   * template inside the school's standard message, with `options.subject` as the message's heading.
   */
  abstract preview(
    organisationId: number,
    html: string,
    includeOrganisationFooter?: boolean,
    options?: CommunicationTemplatePreviewOptions
  ): Observable<CommunicationTemplatePreviewResult>;
  abstract uploadImage(organisationId: number, file: File): Observable<{ url: string }>;
  /**
   * Sends a test email of a saved template with sample merge data (SB-14894). Custom and system templates alike are
   * sent with the school's real data and sample recipient and event values marked "(sample)", as Preview shows
   * them. A custom template is sent inside the school's standard message, and is refused with a label key when the
   * school has none (SB-17220).
   */
  abstract testSend(organisationId: number, request: TestSendRequest): Observable<{ sent: boolean }>;
  /** Whether the "Send test" feature is enabled for this environment/region (SB-14894 gate). */
  abstract isTestSendEnabled(organisationId: number): Observable<{ enabled: boolean }>;

  // ---- Organisation email footer (SB-14896) ----

  /**
   * The organisation's email footer, or `null` when it has none — which is the normal state, so
   * both implementations must resolve a missing footer to `null` rather than erroring.
   */
  abstract getEmailFooter(organisationId: number): Observable<OrganisationEmailFooter | null>;

  /**
   * Saves the footer idempotently (no create-vs-update decision for the caller). An empty string
   * clears it.
   *
   * Returns the **sanitised** value that was actually stored: saving strips comments and
   * `<style>`, so the editor must adopt what comes back and tell the author when it differs from
   * what they submitted, instead of silently diverging from the stored value.
   */
  abstract saveEmailFooter(organisationId: number, html: string): Observable<OrganisationEmailFooter>;

  /** The read-only compliance block appended below the footer on every email. */
  abstract getEmailFooterCompliance(organisationId: number): Observable<EmailFooterCompliance>;

  // ---- Organisation Reply-To default (SB-15933) ----

  /**
   * The organisation's Reply-To default, or `null` when it has never saved one — the normal starting
   * state, so both implementations must resolve a missing row to `null` rather than erroring.
   */
  abstract getReplyToSettings(organisationId: number): Observable<OrganisationEmailReplyToSettings | null>;

  /**
   * Saves the Reply-To default idempotently (PUT upserts, so there is no create-vs-update decision for
   * the caller). Returning to `ReplyToMode.Inherit` is how it is cleared — there is no delete.
   *
   * Returns what was actually stored: the server normalises the request (a Custom mode with no address
   * becomes Inherit, and any other mode clears the address and name), so the panel must adopt the
   * response rather than assume its own form state was kept verbatim.
   */
  abstract saveReplyToSettings(
    organisationId: number,
    settings: OrganisationEmailReplyToSettings
  ): Observable<OrganisationEmailReplyToSettings>;

  // ---- User-created templates (SB-14890) ----
  abstract getUserTemplates(organisationId: number): Observable<CommunicationUserTemplateSummary[]>;
  /**
   * The curated stock templates this organisation can copy from (SB-15889). Still scoped by
   * `organisationId`: the entries visible depend on the organisation's group, whose entries override the
   * global ones.
   */
  abstract getLibraryGallery(organisationId: number): Observable<CommunicationLibraryEntrySummary[]>;
  /** Copies a stock library entry into a template of the organisation's own (SB-15889). */
  abstract createUserTemplateFromLibrary(
    organisationId: number,
    request: CreateUserTemplateFromLibraryRequest
  ): Observable<CommunicationTemplate>;
  abstract getUserTemplate(organisationId: number, id: number): Observable<CommunicationTemplate>;
  abstract createUserTemplate(organisationId: number, request: SaveUserTemplateRequest): Observable<CommunicationTemplate>;
  abstract saveUserTemplate(organisationId: number, id: number, request: SaveUserTemplateRequest): Observable<CommunicationTemplate>;
  abstract duplicateTemplate(organisationId: number, request: DuplicateTemplateRequest): Observable<CommunicationTemplate>;
  abstract deleteUserTemplate(organisationId: number, id: number): Observable<unknown>;
  abstract setUserTemplateTags(organisationId: number, id: number, tags: string[]): Observable<unknown>;
  abstract getUserTemplateVersions(organisationId: number, id: number): Observable<CommunicationTemplateVersionSummary[]>;
  abstract createUserTemplateVersion(
    organisationId: number,
    id: number,
    request: SaveCommunicationTemplateVersionRequest,
    activate: boolean
  ): Observable<CommunicationTemplateVersion>;
  abstract activateUserTemplateVersion(organisationId: number, id: number, versionId: number): Observable<unknown>;
}
